// ResolutionsModal — drives the "what to do about" decisions after a Tiller
// upload. Two sections:
//
//   1. Missing app accounts: per-row radio (Keep as Tiller / Move to Manual)
//      with optional reason. Bulk-select supported.
//   2. Unmatched Tiller accounts: per-row dropdown (Link to existing app
//      account / Ignore). Bulk-select supported.
//
// Submit posts a UploadResolutionsRequest body to /uploads/:id/resolutions.

import { useEffect, useMemo, useState } from 'react';
import type {
  AccountListItem,
  MissingAccountDecision,
  MissingAppAccount,
  UnmatchedTillerAccount,
  UnmatchedTillerDecision,
  UploadResolutionsRequest,
} from '@frt/shared-types';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { formatBalance } from '@/lib/format';

export interface ResolutionsModalProps {
  open: boolean;
  uploadId: number;
  filename: string;
  missing: MissingAppAccount[];
  unmatched: UnmatchedTillerAccount[];
  /** All app accounts the user can pick from for `link` decisions. */
  appAccounts: AccountListItem[];
  /** Disabled while submission is in flight; prevents accidental double submit. */
  submitting?: boolean;
  errorMessage?: string | null;
  onSubmit: (body: UploadResolutionsRequest) => void;
  onCancel: () => void;
}

type MissingAction = 'keep_tiller' | 'move_to_manual';
type UnmatchedAction = 'link' | 'ignore';

interface MissingState {
  action: MissingAction;
  reason: string;
}
interface UnmatchedState {
  action: UnmatchedAction;
  target_account_id: number | null;
  reason: string;
}

export function ResolutionsModal({
  open,
  uploadId,
  filename,
  missing,
  unmatched,
  appAccounts,
  submitting,
  errorMessage,
  onSubmit,
  onCancel,
}: ResolutionsModalProps) {
  const [missingState, setMissingState] = useState<Record<number, MissingState>>({});
  const [unmatchedState, setUnmatchedState] = useState<Record<string, UnmatchedState>>({});

  // Initialize sensible defaults when the modal opens
  useEffect(() => {
    if (!open) return;
    const m: Record<number, MissingState> = {};
    for (const row of missing) {
      m[row.account_id] = { action: 'keep_tiller', reason: '' };
    }
    setMissingState(m);
    const u: Record<string, UnmatchedState> = {};
    for (const row of unmatched) {
      u[row.tiller_account_id] = { action: 'ignore', target_account_id: null, reason: '' };
    }
    setUnmatchedState(u);
  }, [open, missing, unmatched]);

  const sortedAppAccounts = useMemo(
    () => [...appAccounts].sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [appAccounts],
  );

  /** Build a per-Tiller-row dropdown order: app accounts whose
   *  account_number_last4 matches the unmatched row's last4 float to the
   *  top, with a "(last4 match)" indicator. Last4 is a strong hint because
   *  Tiller and the bank both expose the same trailing 4 digits, but it's
   *  not authoritative — operators still see and can pick any account. */
  const orderForRow = (rowLast4: string | null): { acct: AccountListItem; isMatch: boolean }[] => {
    if (!rowLast4) {
      return sortedAppAccounts.map((a) => ({ acct: a, isMatch: false }));
    }
    const matches: AccountListItem[] = [];
    const others: AccountListItem[] = [];
    for (const a of sortedAppAccounts) {
      if (a.account_number_last4 === rowLast4) matches.push(a);
      else others.push(a);
    }
    return [
      ...matches.map((acct) => ({ acct, isMatch: true })),
      ...others.map((acct) => ({ acct, isMatch: false })),
    ];
  };

  if (!open) return null;

  const handleSubmit = () => {
    const missing_account_decisions: MissingAccountDecision[] = missing.map((m) => ({
      account_id: m.account_id,
      action: missingState[m.account_id]?.action ?? 'keep_tiller',
      reason: missingState[m.account_id]?.reason || null,
    }));
    const unmatched_tiller_decisions: UnmatchedTillerDecision[] = unmatched.map((u) => {
      const s = unmatchedState[u.tiller_account_id];
      const action = s?.action ?? 'ignore';
      return {
        tiller_account_id: u.tiller_account_id,
        action,
        ...(action === 'link' && s?.target_account_id
          ? {
              target_account_id: s.target_account_id,
              tiller_account_reference: u.tiller_account_reference ?? undefined,
            }
          : {}),
        reason: s?.reason || null,
      };
    });
    onSubmit({ missing_account_decisions, unmatched_tiller_decisions });
  };

  const linkRowsValid = unmatched.every(
    (u) =>
      unmatchedState[u.tiller_account_id]?.action !== 'link' ||
      !!unmatchedState[u.tiller_account_id]?.target_account_id,
  );

  // Bulk-set helpers
  const bulkSetMissing = (action: MissingAction) => {
    setMissingState((prev) => {
      const next = { ...prev };
      for (const m of missing) next[m.account_id] = { ...next[m.account_id], action, reason: prev[m.account_id]?.reason ?? '' };
      return next;
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <Card className="w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader className="flex items-center justify-between">
          <CardTitle>
            Review upload #{uploadId} — {filename}
          </CardTitle>
          <button
            onClick={onCancel}
            className="text-sm text-slate-500 hover:text-slate-900"
            aria-label="Close"
          >
            ✕
          </button>
        </CardHeader>
        <CardBody className="space-y-6 overflow-auto">
          {/* ---- Missing app accounts ---- */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-semibold text-slate-900">
                Missing app accounts ({missing.length})
              </h3>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => bulkSetMissing('keep_tiller')}>
                  All: Keep as Tiller
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => bulkSetMissing('move_to_manual')}
                >
                  All: Move to Manual
                </Button>
              </div>
            </div>
            {missing.length === 0 ? (
              <p className="text-sm text-slate-500">
                Every previously-active Tiller account was found in this upload.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-slate-500">
                    <tr>
                      <th className="py-1 pr-3">Account</th>
                      <th className="py-1 pr-3">Tiller ID</th>
                      <th className="py-1 pr-3">Decision</th>
                      <th className="py-1 pr-3">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missing.map((m) => {
                      const s = missingState[m.account_id] ?? {
                        action: 'keep_tiller' as const,
                        reason: '',
                      };
                      return (
                        <tr key={m.account_id} className="border-t border-slate-100 align-top">
                          <td className="py-2 pr-3">
                            <div className="font-medium text-slate-900">{m.display_name}</div>
                            <div className="text-xs text-slate-500">
                              {m.tiller_account_reference ?? '(no reference)'}
                            </div>
                          </td>
                          <td className="py-2 pr-3 font-mono text-xs">
                            {m.tiller_account_id.slice(0, 12)}…
                          </td>
                          <td className="py-2 pr-3">
                            <div className="flex flex-col gap-1">
                              <label className="inline-flex items-center gap-1">
                                <input
                                  type="radio"
                                  name={`missing-${m.account_id}`}
                                  checked={s.action === 'keep_tiller'}
                                  onChange={() =>
                                    setMissingState((p) => ({
                                      ...p,
                                      [m.account_id]: { ...s, action: 'keep_tiller' },
                                    }))
                                  }
                                />
                                Keep as Tiller
                              </label>
                              <label className="inline-flex items-center gap-1">
                                <input
                                  type="radio"
                                  name={`missing-${m.account_id}`}
                                  checked={s.action === 'move_to_manual'}
                                  onChange={() =>
                                    setMissingState((p) => ({
                                      ...p,
                                      [m.account_id]: { ...s, action: 'move_to_manual' },
                                    }))
                                  }
                                />
                                Move to Manual
                              </label>
                            </div>
                          </td>
                          <td className="py-2 pr-3">
                            <Input
                              type="text"
                              placeholder="Optional"
                              value={s.reason}
                              onChange={(e) =>
                                setMissingState((p) => ({
                                  ...p,
                                  [m.account_id]: { ...s, reason: e.target.value },
                                }))
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ---- Unmatched Tiller accounts ---- */}
          <section>
            <h3 className="text-base font-semibold text-slate-900 mb-2">
              Unmatched Tiller accounts ({unmatched.length})
            </h3>
            {unmatched.length === 0 ? (
              <p className="text-sm text-slate-500">
                Every Tiller account in this upload is already mapped to an app account.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-slate-500">
                    <tr>
                      <th className="py-1 pr-3">Tiller account</th>
                      <th className="py-1 pr-3">Latest balance</th>
                      <th className="py-1 pr-3">Decision</th>
                      <th className="py-1 pr-3">Link target</th>
                      <th className="py-1 pr-3">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatched.map((u) => {
                      const s = unmatchedState[u.tiller_account_id] ?? {
                        action: 'ignore' as const,
                        target_account_id: null,
                        reason: '',
                      };
                      return (
                        <tr key={u.tiller_account_id} className="border-t border-slate-100 align-top">
                          <td className="py-2 pr-3">
                            <div className="font-medium text-slate-900">
                              {u.tiller_account_reference ?? '(unnamed)'}
                            </div>
                            <div className="text-xs text-slate-500">
                              {u.institution ?? ''}{' '}
                              {u.account_number_last4
                                ? `· …${u.account_number_last4}`
                                : ''}
                            </div>
                          </td>
                          <td className="py-2 pr-3 font-mono text-xs text-right">
                            {formatBalance(u.latest_balance)}
                          </td>
                          <td className="py-2 pr-3">
                            <select
                              className={cn(
                                'rounded border border-slate-300 bg-white px-2 py-1 text-sm',
                                'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
                              )}
                              value={s.action}
                              onChange={(e) =>
                                setUnmatchedState((p) => ({
                                  ...p,
                                  [u.tiller_account_id]: {
                                    ...s,
                                    action: e.target.value as UnmatchedAction,
                                  },
                                }))
                              }
                            >
                              <option value="ignore">Ignore</option>
                              <option value="link">Link to existing</option>
                            </select>
                          </td>
                          <td className="py-2 pr-3">
                            {s.action === 'link' ? (
                              <select
                                className={cn(
                                  'w-64 rounded border border-slate-300 bg-white px-2 py-1 text-sm',
                                  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
                                  s.target_account_id ? '' : 'border-amber-400',
                                )}
                                value={s.target_account_id ?? ''}
                                onChange={(e) =>
                                  setUnmatchedState((p) => ({
                                    ...p,
                                    [u.tiller_account_id]: {
                                      ...s,
                                      target_account_id: e.target.value
                                        ? Number(e.target.value)
                                        : null,
                                    },
                                  }))
                                }
                              >
                                <option value="">— pick app account —</option>
                                {orderForRow(u.account_number_last4).map(({ acct: a, isMatch }) => (
                                  <option key={a.id} value={a.id}>
                                    {a.display_name}
                                    {isMatch ? ' (last4 match)' : ''}
                                    {a.tiller_account_id ? ' · already linked' : ''}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-3">
                            <Input
                              type="text"
                              placeholder="Optional"
                              value={s.reason}
                              onChange={(e) =>
                                setUnmatchedState((p) => ({
                                  ...p,
                                  [u.tiller_account_id]: { ...s, reason: e.target.value },
                                }))
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {errorMessage && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {errorMessage}
            </div>
          )}
        </CardBody>
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting || !linkRowsValid}
          >
            {submitting ? 'Applying…' : 'Apply resolutions'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
